if (!customElements.get('cart-drawer-upsell')) {
  class CartDrawerUpsell extends HTMLElement {
    constructor() {
      super();

      this.recommendedProducts = [];
      this.currentProductIndex = 0;
      this.currentCartProductId = null;
      this.isInitialized = false;
    }

    connectedCallback() {
      if (this.isInitialized) return;
      
      this.container = this.querySelector('[data-upsell-container]');
      this.productImage = this.querySelector('[data-upsell-image]');
      this.productLink = this.querySelector('[data-upsell-link]');
      this.productTitle = this.querySelector('[data-upsell-title]');
      this.productPrice = this.querySelector('[data-upsell-price]');
      this.addButton = this.querySelector('[data-upsell-add-button]');

      this.#init();
    }

    disconnectedCallback() {
      if (this.cartObserver) {
        this.cartObserver.disconnect();
      }

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }

      if (this.addButton && this.addToCartHandler) {
        this.addButton.removeEventListener('click', this.addToCartHandler);
      }

      if (this.cartChangeHandler) {
        document.removeEventListener('ajaxProduct:added', this.cartChangeHandler);
        document.removeEventListener('cart:updated', this.cartChangeHandler);
        document.removeEventListener('cart:quantity', this.cartChangeHandler);
      }

      if (this.drawerOpenHandler) {
        document.removeEventListener('cart:open', this.drawerOpenHandler);
      }
    }

    async #init() {
      this.#bindEvents();
      
      // Clear any existing loading state
      this.isAddingToCart = false;
      if (this.addButton) {
        this.#setLoadingState(false);
      }
      
      // Wait a bit to ensure cart DOM is ready
      await new Promise(resolve => setTimeout(resolve, 100));
      await this.#updateRecommendations();
      
      this.isInitialized = true;
      this.setAttribute('data-initialized', 'true');
    }

    #bindEvents() {
      // Create bound handlers for proper cleanup
      this.cartChangeHandler = this.#handleCartChange.bind(this);
      this.addToCartHandler = this.#handleAddToCart.bind(this);
      this.drawerOpenHandler = this.#handleDrawerOpen.bind(this);

      if (this.addButton) {
        this.addButton.addEventListener('click', this.addToCartHandler);
      }

      document.addEventListener('ajaxProduct:added', this.cartChangeHandler);
      document.addEventListener('cart:updated', this.cartChangeHandler);
      document.addEventListener('cart:quantity', this.cartChangeHandler);
      document.addEventListener('cart:open', this.drawerOpenHandler);

      this.#setupCartObserver();
    }

    async #handleDrawerOpen() {
      // Update recommendations when drawer opens
      await this.#updateRecommendations();
    }

    #setupCartObserver() {
      // Use a more reliable selector - the cart items container
      const setupObserver = () => {
        const cartProductsContainer = document.querySelector('[data-products]');
        
        if (!cartProductsContainer) {
          // Retry in a moment if container not found yet
          setTimeout(setupObserver, 100);
          return;
        }

        if (!window.MutationObserver) return;

        // Disconnect existing observer if any
        if (this.cartObserver) {
          this.cartObserver.disconnect();
        }

        this.cartObserver = new MutationObserver((mutations) => {
          let shouldUpdate = false;

          for (const mutation of mutations) {
            // Check for DOM changes (cart items added/removed/replaced)
            if (mutation.type === 'childList' && mutation.target === cartProductsContainer) {
              shouldUpdate = true;
              break;
            }
            
            // Check for attribute changes on the .cart__items child element
            if (mutation.type === 'attributes' && 
                mutation.target.classList.contains('cart__items') &&
                (mutation.attributeName === 'data-count' || mutation.attributeName === 'data-cart-subtotal')) {
              shouldUpdate = true;
              break;
            }
          }

          if (shouldUpdate) {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = setTimeout(() => {
              this.#updateRecommendations();
            }, 400);
          }
        });

        this.cartObserver.observe(cartProductsContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-count', 'data-cart-subtotal']
        });
      };

      setupObserver();
    }

    async #updateRecommendations() {
      try {
        const cart = await this.#getCart();

        if (!cart.items || cart.items.length === 0) {
          this.#hideWidget();
          return;
        }

        const latestProduct = cart.items[cart.items.length - 1];
        
        if (this.currentCartProductId === latestProduct.product_id && this.recommendedProducts.length > 0) {
          await this.#showNextAvailableProduct(cart);
          return;
        }

        this.currentCartProductId = latestProduct.product_id;
        this.currentProductIndex = 0;

        const recommendations = await this.#fetchRecommendations(latestProduct.product_id);
        
        if (!recommendations || recommendations.length === 0) {
          this.#hideWidget();
          return;
        }

        this.recommendedProducts = recommendations;
        await this.#showNextAvailableProduct(cart);

      } catch (error) {
        this.#hideWidget();
      }
    }

    async #showNextAvailableProduct(cart) {
      const cartProductIds = cart.items.map(item => item.product_id.toString());
      
      let foundProduct = null;
      let searchIndex = this.currentProductIndex;

      for (let i = 0; i < this.recommendedProducts.length; i++) {
        const product = this.recommendedProducts[searchIndex];
        
        if (!cartProductIds.includes(product.id.toString())) {
          foundProduct = product;
          this.currentProductIndex = searchIndex;
          break;
        }
        
        searchIndex = (searchIndex + 1) % this.recommendedProducts.length;
        
        if (searchIndex === this.currentProductIndex) {
          break;
        }
      }

      if (foundProduct) {
        await this.#renderProduct(foundProduct);
        this.#showWidget();
      } else {
        this.#hideWidget();
      }
    }

    async #fetchRecommendations(productId) {
      try {
        const url = `/recommendations/products.json?product_id=${productId}&limit=6&intent=related`;
        const response = await fetch(url);
        
        if (!response.ok) {
          return [];
        }

        const data = await response.json();
        return data.products || [];
      } catch (error) {
        return [];
      }
    }

    async #renderProduct(product) {
      if (!product) return;

      if (this.productImage && product.featured_image) {
        this.productImage.src = product.featured_image;
        this.productImage.alt = product.title;
      }

      if (this.productLink) {
        this.productLink.href = product.url;
      }

      if (this.productTitle) {
        this.productTitle.textContent = product.title;
        this.productTitle.href = product.url;
      }

      if (this.productPrice && product.price) {
        this.productPrice.innerHTML = this.#formatPrice(product.price, product.compare_at_price);
      }

      if (this.addButton && product.variants && product.variants.length > 0) {
        this.addButton.setAttribute('data-variant-id', product.variants[0].id || '');
        this.addButton.disabled = !product.available;
      }
    }

    async #handleAddToCart(event) {
      event.preventDefault();

      const variantId = this.addButton.getAttribute('data-variant-id');
      if (!variantId || this.addButton.disabled) return;

      this.#setLoadingState(true);
      this.isAddingToCart = true;

      // Set a timeout to clear loading state as fallback
      const loadingTimeout = setTimeout(() => {
        if (this.isAddingToCart) {
          console.warn('Cart upsell: Loading state timeout, clearing manually');
          this.isAddingToCart = false;
          this.#setLoadingState(false);
        }
      }, 10000); // 10 second timeout

      try {
        // Add product to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const responseData = await response.json();

        // Update current index to show next product
        this.currentProductIndex = (this.currentProductIndex + 1) % this.recommendedProducts.length;

        // Dispatch events to trigger cart rebuild
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { 
            product: responseData,
            addToCartBtn: this.addButton 
          }
        }));

        // Clear loading state immediately after successful add
        clearTimeout(loadingTimeout);
        this.isAddingToCart = false;
        this.#setLoadingState(false);

        // The cart drawer will rebuild via the ajaxProduct:added event
        // Then our cart:updated listener will update recommendations

      } catch (error) {
        console.error('Error adding product to cart:', error);
        clearTimeout(loadingTimeout);
        this.isAddingToCart = false;
        this.#setLoadingState(false);
      }
    }

    async #handleCartChange(event) {
      // Add small delay to ensure cart is fully updated
      clearTimeout(this.updateTimeout);
      this.updateTimeout = setTimeout(async () => {
        await this.#updateRecommendations();
        
        // Clear loading state if we were adding to cart
        if (this.isAddingToCart) {
          this.isAddingToCart = false;
          this.#setLoadingState(false);
        }
      }, 500);
    }

    async #getCart() {
      try {
        const response = await fetch('/cart.js');
        return await response.json();
      } catch (error) {
        return { items: [] };
      }
    }

    #setLoadingState(loading) {
      if (!this.addButton) return;
      
      if (loading) {
        this.addButton.classList.add('cart-drawer-upsell__button--loading');
        this.addButton.disabled = true;
      } else {
        this.addButton.classList.remove('cart-drawer-upsell__button--loading');
        const variantId = this.addButton.getAttribute('data-variant-id');
        this.addButton.disabled = !variantId;
      }
    }

    #showWidget() {
      if (this.container) {
        this.container.style.display = '';
      }
    }

    #hideWidget() {
      if (this.container) {
        this.container.style.display = 'none';
      }
    }

    #formatPrice(price, compareAtPrice) {
      const formatMoney = (cents) => {
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents);
        }
        if (window.theme && window.theme.Currency && window.theme.settings && window.theme.settings.moneyFormat) {
          return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
        }
        return `${(cents / 100).toFixed(2)} €`;
      };

      let priceHtml = `<span class="cart-drawer-upsell__price-value">${formatMoney(price)}</span>`;

      if (compareAtPrice && compareAtPrice > price) {
        priceHtml += ` <span class="cart-drawer-upsell__price-compare-at">${formatMoney(compareAtPrice)}</span>`;
      }

      return priceHtml;
    }
  }

  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
